import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";
import { BRAND_LOGO, BRAND_NAME } from "@/lib/brand";
import { getPinEmail, clearPinEmail } from "@/lib/pp_device";
import PinPad from "@/components/PinPad";
import SetupPinModal from "@/components/SetupPinModal";
import PasswordInput from "@/components/PasswordInput";
import { Key, LockKey, ArrowLeft, Gear } from "@phosphor-icons/react";

export default function Login() {
  const { login, loginWithPin, checkPinStatus } = useAuth();
  const navigate = useNavigate();
  const rememberedPinEmail = getPinEmail();
  // Default mode: PIN-first if we have a remembered account on this device, otherwise password.
  const [mode, setMode] = useState(rememberedPinEmail ? "pin" : "password");
  // Admin mode: toggled via the discreet cog icon. Switches copy + sends staff to /admin
  // after login. Backend still enforces role; this is purely a UX affordance so the team
  // knows she's logging into the back-of-house, not the client portal.
  const [adminMode, setAdminMode] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.location.hash === "#admin" || new URLSearchParams(window.location.search).get("admin") === "1";
  });
  const [email, setEmail] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("email") || "";
  });
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState(null);

  // Warm up the backend as soon as the login page mounts. Many cold-start
  // failures present as "Couldn't reach the server" because the very first
  // request after a deploy / idle has to spin up a dyno. Firing a cheap GET
  // here means by the time the user finishes typing their password, the
  // backend is ready. Fire-and-forget — failures are silent.
  useEffect(() => {
    let cancelled = false;
    api.get("/health", { timeout: 8000 }).catch(() => {
      if (cancelled) return;
      // Fallback: try the docs route which always exists on FastAPI.
      api.get("/", { timeout: 8000 }).catch(() => { /* silent */ });
    });
    return () => { cancelled = true; };
  }, []);

  const goNext = async (u) => {
    // Claim any guest-checkout enrolment hanging off this URL's session_id.
    try {
      const sid = new URLSearchParams(window.location.search).get("session_id");
      if (sid) {
        await api.post(`/payments/claim-pending/${sid}`).catch(() => {});
      }
    } catch { /* ignore */ }
    // Honour ?next=... query param if it's a safe same-origin path.
    // Without this the "Pay & enrol" flow breaks: visitors are redirected to
    // /login?next=/courses/in-person?series=... and we used to drop them on
    // the portal home instead of the class they were trying to book.
    try {
      const sp = new URLSearchParams(window.location.search);
      const nxt = sp.get("next");
      if (nxt && nxt.startsWith("/") && !nxt.startsWith("//")) {
        navigate(nxt);
        return;
      }
      // If we just claimed a paid enrolment, send them straight to bookings.
      if (sp.get("session_id")) {
        navigate("/portal/bookings");
        return;
      }
    } catch { /* fall through to default */ }
    navigate(u.role === "client" ? "/portal" : "/admin");
  };

  const onPasswordSubmit = async (e) => {
    e.preventDefault();
    if (busy) return; // ignore double-submits — primary defence is the disabled attribute, this is belt+braces
    if (!email.trim() || !password) {
      toast.error("Email and password are required");
      return;
    }
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name}!`);
      // First time logging in on this browser? Offer to set a PIN.
      const status = await checkPinStatus();
      if (!status.has_on_device) {
        setPendingRedirect(u);
        setSetupOpen(true);
      } else {
        goNext(u);
      }
    } catch (err) {
      // Distinguish network errors from auth errors so the user knows what to do.
      // Log full detail to console for support diagnostics (browser DevTools).
      // eslint-disable-next-line no-console
      console.error("[login] failure", {
        code: err.code, message: err.message, status: err.response?.status,
        data: err.response?.data, baseURL: process.env.REACT_APP_BACKEND_URL,
      });
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        toast.error("Server is slow to respond — please try again in a moment");
      } else if (!err.response) {
        // Surface the underlying error code so we stop guessing CORS vs DNS vs offline.
        const code = err.code || "NETWORK";
        toast.error(`Couldn't reach the server (${code}). If this keeps happening, tap "Trouble signing in? Reset app" below.`);
      } else if (err.response.status === 401) {
        toast.error("Incorrect email or password");
      } else if (err.response.status >= 500) {
        toast.error("Server error — please try again in a moment");
      } else {
        toast.error(err.response?.data?.detail || "Login failed — please try again");
      }
    } finally { setBusy(false); }
  };

  const onPinComplete = async (entered) => {
    if (busy) return;
    setBusy(true);
    try {
      const u = await loginWithPin(rememberedPinEmail, entered);
      toast.success(`Welcome back, ${u.name}!`);
      goNext(u);
    } catch (err) {
      let detail = err.response?.data?.detail || "PIN sign-in failed";
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        detail = "Server didn't respond — try password sign-in below";
      } else if (!err.response) {
        detail = "No internet connection — try again";
      }
      toast.error(detail);
      setPin("");
      // Hard fail — let the user see the input is cleared
    } finally { setBusy(false); }
  };

  const switchToPassword = () => {
    setMode("password");
    setPin("");
  };

  const forgetThisDevice = () => {
    clearPinEmail();
    setMode("password");
    toast.message("Removed the saved account from this device");
  };

  /**
   * Hard-reset escape hatch — for stale-cache + post-redeploy issues.
   * Clears all localStorage/sessionStorage, asks the SW to nuke its caches,
   * unregisters the SW, then hard-reloads with a cache-busting query.
   * Used by both the team and clients when the app misbehaves.
   */
  const resetApp = async () => {
    if (!window.confirm("Reset the app on this device? You'll be signed out and any cached data will be cleared. This fixes most post-update issues.")) return;
    try {
      try { localStorage.clear(); } catch { /* ignore */ }
      try { sessionStorage.clear(); } catch { /* ignore */ }
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        const swReady = await navigator.serviceWorker.getRegistration();
        if (swReady?.active) {
          swReady.active.postMessage({ type: "RESET_CACHES" });
        }
        // small delay so the SW can purge caches, then unregister
        await new Promise((r) => setTimeout(r, 400));
        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
      }
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      toast.success("App reset — reloading…");
      setTimeout(() => {
        window.location.replace(window.location.pathname + "?fresh=" + Date.now());
      }, 600);
    } catch (e) {
      toast.error("Reset partially failed — try clearing your browser cache manually.");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="login-logo">
          <img src={BRAND_LOGO} alt={BRAND_NAME} className="h-16 w-auto" />
        </Link>
        <div className="card-pp p-8">
          {mode === "pin" ? (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#33C095]/10 mb-3">
                  <Key size={28} weight="duotone" className="text-[#33C095]" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-stone-900 mb-1">Enter your PIN</h1>
                <p className="text-sm text-stone-500">Signed in as <strong className="text-stone-800">{rememberedPinEmail}</strong></p>
              </div>
              <PinPad value={pin} onChange={setPin} onComplete={onPinComplete} disabled={busy} data-testid="login-pin-pad" />
              <div className="mt-6 text-center space-y-2">
                <button onClick={switchToPassword} className="text-sm font-semibold text-[#1F9577] hover:underline inline-flex items-center gap-1.5" data-testid="login-use-password">
                  <LockKey size={14} weight="bold" /> Use password instead
                </button>
                <div>
                  <button onClick={forgetThisDevice} className="text-xs text-stone-400 hover:text-stone-600" data-testid="login-forget-device">
                    Not you? Forget this device
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {rememberedPinEmail && (
                <button onClick={() => setMode("pin")} className="text-xs text-[#1F9577] hover:underline inline-flex items-center gap-1 mb-3" data-testid="login-back-to-pin">
                  <ArrowLeft size={12} weight="bold" /> Back to PIN
                </button>
              )}
              <h1 className="font-heading text-3xl font-bold text-stone-900 mb-2">
                {adminMode ? "Staff sign in" : "Welcome back"}
              </h1>
              <p className="text-stone-600 mb-6">
                {adminMode
                  ? "Signing in to the admin dashboard."
                  : "Sign in to your client portal."}
              </p>
              <form onSubmit={onPasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Email</label>
                  <input
                    data-testid="login-email" type="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#33C095]/30"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-semibold text-stone-700">Password</label>
                    <Link
                      to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`}
                      className="text-xs font-semibold text-[#1F9577] hover:underline"
                      data-testid="login-forgot-password"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <PasswordInput
                    data-testid="login-password" required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <button data-testid="login-submit" type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50">
                  {busy ? "Signing in..." : "Sign In"}
                </button>
              </form>
              <p className="mt-6 text-sm text-center text-stone-600">
                {adminMode ? (
                  <>
                    Client?{" "}
                    <button
                      type="button"
                      onClick={() => { setAdminMode(false); if (typeof window !== "undefined") window.history.replaceState({}, "", window.location.pathname); }}
                      className="text-[#33C095] font-semibold hover:underline"
                      data-testid="login-exit-admin-mode"
                    >
                      Back to client portal sign in
                    </button>
                  </>
                ) : (
                  <>
                    New here?{" "}
                    <Link to="/register" className="text-[#33C095] font-semibold hover:underline" data-testid="login-to-register">
                      Create an account
                    </Link>
                  </>
                )}
              </p>
            </>
          )}
        </div>
        {/* Discreet admin-mode toggle — clients won't notice it, the team will. */}
        <div className="mt-4 flex flex-col items-center gap-2">
          <button
            type="button"
            aria-label={adminMode ? "Exit staff sign-in mode" : "Switch to staff sign-in"}
            title={adminMode ? "Exit staff sign-in mode" : "Staff sign-in"}
            onClick={() => {
              const next = !adminMode;
              setAdminMode(next);
              if (typeof window !== "undefined") {
                if (next) window.history.replaceState({}, "", window.location.pathname + "#admin");
                else window.history.replaceState({}, "", window.location.pathname);
              }
              setMode("password"); // staff always password
            }}
            className={`p-2 rounded-full transition-colors ${adminMode ? "bg-[#0F1614] text-white shadow" : "text-stone-300 hover:text-[#1F9577] hover:bg-stone-100"}`}
            data-testid="login-admin-cog"
          >
            <Gear size={16} weight={adminMode ? "fill" : "regular"} />
          </button>
          <button
            type="button"
            onClick={resetApp}
            className="text-[11px] text-stone-400 hover:text-stone-600 underline-offset-2 hover:underline"
            data-testid="login-reset-app"
            title="Clears cached app data + signs you out — use if the app is misbehaving after an update."
          >
            Trouble signing in? Reset app
          </button>
        </div>
      </div>

      {setupOpen && (
        <SetupPinModal
          onClose={() => { setSetupOpen(false); if (pendingRedirect) goNext(pendingRedirect); }}
          onSkip={() => { setSetupOpen(false); if (pendingRedirect) goNext(pendingRedirect); }}
        />
      )}
    </div>
  );
}
